// src/pages/DiasporaRechargePage.jsx
// Page publique — accessible sans compte — zenvoi.cm/recharge
// Un parent à Paris peut recharger le wallet de son enfant à Douala

import { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';

const C = {
  bg: '#0A0E1A', surface: '#111827', card: '#1a2235',
  border: '#1e2d45', accent: '#00D4AA', gold: '#F5C542',
  text: '#E8EDF5', muted: '#6b7fa3', red: '#FF4F6A',
};

const XAF_EUR = 655.957;
const xafToEur = (xaf) => (xaf / XAF_EUR).toFixed(2);

const AMOUNTS = [
  { xaf: 1000,  label: '1 000 XAF',  sub: '~1,52FCFA ' },
  { xaf: 2000,  label: '2 000 XAF',  sub: '~3,05FCFA ' },
  { xaf: 5000,  label: '5 000 XAF',  sub: '~7,62FCFA ' },
  { xaf: 10000, label: '10 000 XAF', sub: '~15,25FCFA ' },
  { xaf: 25000, label: '25 000 XAF', sub: '~38,11FCFA ' },
  { xaf: 50000, label: '50 000 XAF', sub: '~76,22FCFA ' },
];

export function DiasporaRechargePage() {
  const [step, setStep]           = useState('form'); // form | success
  const [loading, setLoading]     = useState(false);
  const [searchParams]            = useSearchParams();
  const [form, setForm] = useState({
    amount_xaf:       5000,
    purchaser_name:   '',
    purchaser_email:  '',
    purchaser_phone:  '',
    recipient_phone:  '',
    recipient_name:   '',
    message:          '',
    custom_amount:    '',
  });

  // Callback après paiement Notchpay
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) handleCallback(ref);
  }, []);

  const handleCallback = async (ref) => {
    try {
      const res = await api.get(`/vouchers/diaspora/confirm?ref=${ref}`);
      toast.success(res.message);
      setStep('success');
    } catch (err) {
      toast.error(err.error || 'Confirmation échouée');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = form.custom_amount ? parseInt(form.custom_amount) : form.amount_xaf;
    if (!form.purchaser_email || !form.recipient_phone) {
      return toast.error('Email et numéro du destinataire requis');
    }
    setLoading(true);
    try {
      const res = await api.post('/vouchers/diaspora/initiate', {
        amount_xaf:       amount,
        purchaser_name:   form.purchaser_name,
        purchaser_email:  form.purchaser_email,
        purchaser_phone:  form.purchaser_phone,
        recipient_phone:  form.recipient_phone,
        recipient_name:   form.recipient_name,
        message:          form.message,
      });
      // Rediriger vers Notchpay
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err.error || 'Erreur lors de la demande');
      setLoading(false);
    }
  };

  const selectedAmount = form.custom_amount ? parseInt(form.custom_amount) : form.amount_xaf;

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'Sora', sans-serif", color: C.text }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&display=swap');
        * { box-sizing: border-box; }
        input::placeholder { color: #6b7fa3; }
        input:focus { border-color: #00D4AA !important; outline: none; }
      `}</style>

      {/* Header public */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 24, fontWeight: 800, color: C.accent }}>ZENVOI</div>
        <a href="/login" style={{ color: C.accent, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
          Se connecter →
        </a>
      </div>

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '40px 20px' }}>

        {step === 'success' ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: 72, marginBottom: 20 }}>✅</div>
            <h2 style={{ fontSize: 28, fontWeight: 800, color: C.accent, marginBottom: 12 }}>
              Envoi réussi !
            </h2>
            <p style={{ color: C.muted, lineHeight: 1.8 }}>
              Votre paiement a été confirmé. Le destinataire recevra un SMS avec son code de recharge ou un crédit direct si il est déjà inscrit sur PayChat.
            </p>
            <button onClick={() => setStep('form')} style={{
              marginTop: 32, padding: '14px 32px',
              background: `linear-gradient(90deg, ${C.accent}, #00b894)`,
              border: 'none', borderRadius: 12, cursor: 'pointer',
              color: '#000', fontWeight: 800, fontSize: 15,
              fontFamily: "'Sora', sans-serif",
            }}>
              Faire un nouvel envoi
            </button>
          </div>
        ) : (
          <>
            {/* Hero */}
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <div style={{ display: 'inline-block', background: `${C.accent}22`, border: `1px solid ${C.accent}44`, borderRadius: 20, padding: '6px 18px', fontSize: 12, color: C.accent, fontWeight: 700, marginBottom: 16, letterSpacing: 1 }}>
                ENVOI DIASPORA — SANS INSCRIPTION
              </div>
              <h1 style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-1px', margin: '0 0 12px' }}>
                Rechargez le wallet<br />
                <span style={{ color: C.accent }}>de vos proches au Cameroun</span>
              </h1>
              <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.6, maxWidth: 420, margin: '0 auto' }}>
                Payez par carte bancaire depuis n'importe quel pays. Votre proche reçoit le crédit instantanément.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Montant */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: '24px', marginBottom: 16 }}>
                <label style={S.label}>MONTANT À ENVOYER</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
                  {AMOUNTS.map(a => (
                    <button key={a.xaf} type="button"
                      onClick={() => setForm({ ...form, amount_xaf: a.xaf, custom_amount: '' })}
                      style={{
                        padding: '12px 8px', borderRadius: 12,
                        background: form.amount_xaf === a.xaf && !form.custom_amount ? `${C.accent}22` : C.surface,
                        border: `2px solid ${form.amount_xaf === a.xaf && !form.custom_amount ? C.accent : C.border}`,
                        cursor: 'pointer', textAlign: 'center',
                        fontFamily: "'Sora', sans-serif",
                      }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: form.amount_xaf === a.xaf && !form.custom_amount ? C.accent : C.text }}>
                        {a.label}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{a.sub}</div>
                    </button>
                  ))}
                </div>
                <input value={form.custom_amount}
                  onChange={e => setForm({ ...form, custom_amount: e.target.value, amount_xaf: 0 })}
                  placeholder="Autre montant en XAF..."
                  type="number" min="500"
                  style={{ ...S.input, textAlign: 'center', fontSize: 18, fontWeight: 700 }} />

                {selectedAmount >= 500 && (
                  <div style={{ marginTop: 12, background: `${C.accent}11`, border: `1px solid ${C.accent}33`, borderRadius: 10, padding: '10px 14px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, color: C.muted }}>Vous payez environ</span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: C.accent }}>
                      ~{xafToEur(selectedAmount)}FCFA 
                    </span>
                  </div>
                )}
              </div>

              {/* Destinataire */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: '24px', marginBottom: 16 }}>
                <label style={S.label}>DESTINATAIRE (au Cameroun)</label>
                <input value={form.recipient_name}
                  onChange={e => setForm({ ...form, recipient_name: e.target.value })}
                  placeholder="Prénom et nom du destinataire"
                  style={S.input} />
                <input value={form.recipient_phone}
                  onChange={e => setForm({ ...form, recipient_phone: e.target.value })}
                  placeholder="Numéro de téléphone (+237...)"
                  type="tel" required
                  style={S.input} />
                <input value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  placeholder="Message (optionnel) — ex: Bon anniversaire ! 🎂"
                  style={{ ...S.input, marginBottom: 0 }} />
              </div>

              {/* Vos coordonnées */}
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: '24px', marginBottom: 24 }}>
                <label style={S.label}>VOS COORDONNÉES (pour la confirmation)</label>
                <input value={form.purchaser_name}
                  onChange={e => setForm({ ...form, purchaser_name: e.target.value })}
                  placeholder="Votre prénom et nom"
                  style={S.input} />
                <input value={form.purchaser_email}
                  onChange={e => setForm({ ...form, purchaser_email: e.target.value })}
                  placeholder="Votre email (reçu de confirmation)"
                  type="email" required
                  style={S.input} />
                <input value={form.purchaser_phone}
                  onChange={e => setForm({ ...form, purchaser_phone: e.target.value })}
                  placeholder="Votre téléphone (optionnel)"
                  type="tel"
                  style={{ ...S.input, marginBottom: 0 }} />
              </div>

              <button type="submit" disabled={loading || selectedAmount < 500} style={{
                width: '100%', padding: 16,
                background: selectedAmount >= 500 && !loading
                  ? `linear-gradient(90deg, ${C.accent}, #00b894)` : C.card,
                border: `1px solid ${selectedAmount >= 500 ? C.accent : C.border}`,
                borderRadius: 14, cursor: selectedAmount >= 500 ? 'pointer' : 'not-allowed',
                color: selectedAmount >= 500 && !loading ? '#000' : C.muted,
                fontWeight: 800, fontSize: 16, fontFamily: "'Sora', sans-serif",
                transition: 'all 0.2s',
              }}>
                {loading
                  ? '⏳ Redirection vers le paiement...'
                  : `Envoyer ${selectedAmount >= 500 ? selectedAmount.toLocaleString() + ' XAF' : ''}`}
              </button>

              <div style={{ textAlign: 'center', marginTop: 14, fontSize: 12, color: C.muted, lineHeight: 1.8 }}>
                🔒 Paiement sécurisé par <strong style={{ color: C.accent }}>Notchpay</strong><br/>
                Carte Visa · Mastercard · Orange Money · MTN MoMo<br/>
                Taux fixe officiel : 1FCFA  = 655,957 XAF (zone franc CFA)
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const S = {
  label: {
    fontSize: 11, color: '#6b7fa3', fontWeight: 600,
    letterSpacing: 1, textTransform: 'uppercase',
    marginBottom: 10, display: 'block',
  },
  input: {
    width: '100%', background: '#111827',
    border: '1px solid #1e2d45', borderRadius: 10,
    padding: '11px 14px', color: '#E8EDF5', fontSize: 14,
    fontFamily: "'Sora', sans-serif", marginBottom: 10,
    transition: 'border-color 0.2s',
  },
};
